import React, { useEffect, useState } from 'react';
import { Portfolios, OpenSourcePackages } from '../utils/Data';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt, FaVolumeUp, FaGithub, FaCopy, FaCheck, FaBoxOpen, FaTiktok } from 'react-icons/fa';
import { SiNpm } from 'react-icons/si';
import { fetchNpmStats, NpmStats } from '../utils/npmApi';
import WhatsAppLink from '../utils/WhatsappLink';

function formatTimeAgo(dateString: string): string {
  if (!dateString) return 'unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 1) return 'today';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

function formatDownloads(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
}

function PackageStats({ packageName, isPublished }: { packageName: string; isPublished: boolean }) {
  const [stats, setStats] = useState<NpmStats | null>(null);
  const [loading, setLoading] = useState(isPublished);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isPublished) return;
    
    let isMounted = true;
    fetchNpmStats(packageName)
      .then(data => {
        if (isMounted) {
          setStats(data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (isMounted) {
          setError(true);
          setLoading(false);
          console.error(err);
        }
      });
      
    return () => {
      isMounted = false;
    };
  }, [packageName, isPublished]);

  if (!isPublished) {
    return (
      <div className="grid grid-cols-2 gap-4 py-3 border-y border-slate-100 dark:border-slate-900/60 text-xs text-slate-500">
        <div>
          <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Downloads</span>
          <span className="font-semibold text-slate-600 dark:text-slate-400 code-font">--</span>
        </div>
        <div>
          <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">License</span>
          <span className="font-semibold text-slate-600 dark:text-slate-400 code-font">MIT</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-4 gap-4 py-3 border-y border-slate-100 dark:border-slate-900/60 animate-pulse text-slate-400">
        <div className="col-span-2">
          <div className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded"></div>
          <div className="h-4 w-12 bg-slate-300 dark:bg-slate-700 rounded mt-1"></div>
        </div>
        <div className="col-span-2">
          <div className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded"></div>
          <div className="h-4 w-12 bg-slate-300 dark:bg-slate-700 rounded mt-1"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="grid grid-cols-2 gap-4 py-3 border-y border-slate-100 dark:border-slate-900/60 text-xs text-slate-500">
        <div>
          <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Downloads</span>
          <span className="text-[10px] text-slate-400 italic">Stats unavailable</span>
        </div>
        <div>
          <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">License</span>
          <span className="font-semibold text-slate-600 dark:text-slate-400 code-font">MIT</span>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-3 border-y border-slate-100 dark:border-slate-900/60 text-xs">
      <div>
        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Weekly Downloads</span>
        <motion.span 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-bold text-teal-600 dark:text-teal-400 code-font text-sm"
        >
          {formatDownloads(stats.weeklyDownloads)}
        </motion.span>
      </div>
      <div>
        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Version</span>
        <span className="font-semibold text-slate-700 dark:text-slate-300 code-font">
          v{stats.version}
        </span>
      </div>
      <div>
        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Last Published</span>
        <span className="font-semibold text-slate-700 dark:text-slate-300">
          {formatTimeAgo(stats.lastPublished)}
        </span>
      </div>
      <div>
        <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">License</span>
        <span className="font-semibold text-slate-700 dark:text-slate-300 code-font">
          {stats.license}
        </span>
      </div>
    </div>
  );
}

function Portfolio() {
  const [activeTemplate, setActiveTemplate] = useState("Secret Crush 💖");
  const [mockMessage, setMockMessage] = useState("");
  const [voiceEffect, setVoiceEffect] = useState("Normal 👤");
  const [showNotification, setShowNotification] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  // Open Source Demos Interactive States
  const [progressVal, setProgressVal] = useState(75);
  const [editorText, setEditorText] = useState("Hello world! Try editing this text or using the formatting controls above...");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [editorStats, setEditorStats] = useState({ chars: 81, words: 12 });
  const [copiedText, setCopiedText] = useState("");

  useEffect(() => {
    const words = editorText.trim() === "" ? 0 : editorText.trim().split(/\s+/).length;
    setEditorStats({
      chars: editorText.length,
      words: words
    });
  }, [editorText]);

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(""), 2000);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleConfessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  const handlePlayVoiceMock = () => {
    setIsPlayingAudio(true);
    setTimeout(() => setIsPlayingAudio(false), 2500);
  };

  // Separate Confess from other projects
  const confessProject = Portfolios.find(p => p.featured);
  const otherProjects = Portfolios.filter(p => !p.featured);

  return (
    <div className="relative min-h-screen pt-28 pb-16 overflow-hidden bg-grid-pattern">
      <div className="glow-spot top-1/4 left-1/4 -translate-x-1/2"></div>
      <div className="glow-spot bottom-1/4 right-1/4 translate-x-1/2"></div>

      <div className="max-w-6xl mx-auto w-full px-5 md:px-10 lg:px-24 relative z-10 space-y-16">
        
        {/* Title */}
        <div className="text-start space-y-4">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Projects & Products
          </h1>
          <div className="h-1.5 w-20 bg-teal-500 rounded-full"></div>
          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base max-w-xl">
            A selective breakdown of web products, open-source libraries, and applications I have engineered and shipped.
          </p>
        </div>

        {/* FLAGSHIP FEATURED PRODUCT: CONFESS */}
        {confessProject && (
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-start space-y-6"
          >
            <div className="text-xs font-bold uppercase tracking-wider text-teal-600 dark:text-teal-400 code-font">
              🚀 Flagship Product Showcase
            </div>

            <div className="p-6 md:p-8 rounded-3xl glass border border-teal-500/20 bg-teal-500/[0.01] grid grid-cols-1 lg:grid-cols-12 gap-8 items-start shadow-xl">
              
              {/* Product Info Column */}
              <div className="lg:col-span-7 space-y-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                    {confessProject.name}
                  </h2>
                  <span className="px-2.5 py-1 rounded-full border border-teal-500/20 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-[10px] font-bold uppercase tracking-wider code-font">
                    {confessProject.tagline}
                  </span>
                </div>

                <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                  {confessProject.desc}
                </p>

                {/* Features list */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 code-font">
                    Key Features Built
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm text-slate-600 dark:text-slate-400 leading-normal pl-4 list-disc">
                    {confessProject.features?.map((feat, idx) => (
                      <li key={idx} className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {confessProject.tech?.map((tag, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="pt-2">
                  <a 
                    href={confessProject.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-semibold text-xs shadow-md transition-colors"
                  >
                    Launch Live Platform <FaExternalLinkAlt size={10} />
                  </a>
                </div>
              </div>

              {/* Interactive Widget Column (Mobile Phone Simulator) */}
              <div className="lg:col-span-5 w-full flex justify-center">
                <div className="w-full max-w-[320px] rounded-3xl border-4 border-slate-300 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 shadow-lg relative text-xs">
                  {/* Speaker Notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-slate-300 dark:bg-slate-800 rounded-full flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-950/40 block"></span>
                  </div>

                  {/* Simulator Screen */}
                  <div className="mt-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 min-h-[300px] flex flex-col justify-between">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                        <span className="font-bold text-teal-600 dark:text-teal-400 code-font">Confess API Mock</span>
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      </div>

                      {/* Select Template */}
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Template</label>
                        <div className="flex gap-1 overflow-x-auto pb-1">
                          {["Secret Crush 💖", "Apology 🎭", "Compliment 🌟"].map(t => (
                            <button 
                              key={t}
                              type="button" 
                              onClick={() => {
                                setActiveTemplate(t);
                                if (t.includes('Crush')) setMockMessage("I've liked you since our first Web3 project session...");
                                if (t.includes('Apology')) setMockMessage("Sorry for breaking the build branch last night!");
                                if (t.includes('Compliment')) setMockMessage("Your CSS animations are absolutely premium!");
                              }}
                              className={`px-2 py-1 rounded text-[9px] whitespace-nowrap border transition-colors ${
                                activeTemplate === t 
                                  ? 'bg-teal-500 border-teal-500 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400' 
                                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                              }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Text Input */}
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Your Secret Message</label>
                        <textarea 
                          value={mockMessage} 
                          onChange={(e) => setMockMessage(e.target.value)}
                          placeholder="Type an anonymous confession..."
                          rows={3} 
                          className="w-full p-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 outline-none text-[10px] text-slate-700 dark:text-slate-300 focus:border-teal-500"
                        />
                      </div>

                      {/* Voice Settings */}
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase">Simulated Voice Filter</label>
                        <div className="grid grid-cols-3 gap-1">
                          {["Normal 👤", "Chipmunk 🐿️", "Vader 👤"].map(v => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setVoiceEffect(v)}
                              className={`py-1 rounded text-[9px] border transition-colors ${
                                voiceEffect === v 
                                  ? 'bg-teal-500 border-teal-500 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400'
                                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                              }`}
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                      {/* Audio voice preview */}
                      <button 
                        type="button" 
                        onClick={handlePlayVoiceMock}
                        className="w-full py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-center gap-1.5 font-semibold text-[10px] text-slate-600 dark:text-slate-300 cursor-pointer"
                      >
                        <FaVolumeUp className={isPlayingAudio ? 'animate-bounce text-teal-500' : ''} />
                        <span>{isPlayingAudio ? "Simulating voice pitch..." : "Preview Transformed Voice"}</span>
                      </button>

                      <button 
                        onClick={handleConfessSubmit} 
                        className="w-full py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-bold text-[10px] tracking-wide transition-colors cursor-pointer"
                      >
                        Send Anonymous Confession
                      </button>
                    </div>
                  </div>

                  {/* Toast Notification inside Mockup */}
                  <AnimatePresence>
                    {showNotification && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute inset-x-8 bottom-8 p-3 rounded-xl bg-slate-900 text-white border border-slate-800 shadow-2xl flex flex-col items-center justify-center text-center space-y-1"
                      >
                        <span className="font-bold text-teal-400">✨ Packaged Successfully!</span>
                        <p className="text-[9px] text-slate-400">Packaged with 3D floating hearts and voice transformation. Ready to ship!</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

            </div>
          </motion.section>
        )}

        {/* OTHER PORTFOLIO PROJECTS */}
        <section className="space-y-6 text-start">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 code-font">
            💻 Core Projects & Contributions
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherProjects.map((port, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative rounded-2xl glass overflow-hidden border border-slate-200/80 dark:border-slate-800/80 bg-white/40 dark:bg-slate-950/40 hover:border-teal-500/20 transition-all duration-300 flex flex-col justify-between"
              >
                {/* Visual Image container with hover zoom */}
                <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-slate-200/80 dark:border-slate-800/80 bg-slate-100 dark:bg-slate-900">
                  <img 
                    src={port.image} 
                    alt={port.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-slate-950/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <a 
                      href={port.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="px-4 py-2 bg-white text-slate-900 dark:bg-slate-900 dark:text-white rounded-full font-bold text-xs shadow-md flex items-center gap-1.5 hover:scale-105 transition-all"
                    >
                      Visit Website <FaExternalLinkAlt size={10} />
                    </a>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {port.name}
                      </h3>
                      <span className="text-[9px] code-font text-slate-400 font-medium uppercase tracking-wider">
                        {port.tagline}
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      {port.desc}
                    </p>
                  </div>

                  {/* Bullet features */}
                  {port.features && (
                    <div className="space-y-1.5">
                      <ul className="list-disc pl-4 space-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                        {port.features.map((f, fi) => (
                          <li key={fi}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tech stack badges */}
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {port.tech?.map((t, ti) => (
                      <span key={ti} className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-900 text-[10px] font-medium text-slate-500 dark:text-slate-400">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* OPEN SOURCE & NPM PACKAGES */}
        <section id="open-source" className="space-y-6 text-start pt-10 border-t border-slate-200/50 dark:border-slate-800/50">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaBoxOpen className="text-teal-500" size={24} />
              Open Source & npm Packages
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs md:text-sm max-w-xl">
              I enjoy building reusable tools and open-source packages that solve real problems for developers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4">
            {OpenSourcePackages.map((pkg, index) => {
              const isProgressiveShapes = pkg.name === "progressive-shapes";
              const isPublished = pkg.status === "published";
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative rounded-2xl glass overflow-hidden border border-slate-200/80 dark:border-slate-800/80 bg-white/40 dark:bg-slate-950/40 hover:border-teal-500/20 transition-all duration-300 flex flex-col justify-between p-6 space-y-6"
                >
                  {/* Top Header / Metadata */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-teal-500/10 dark:bg-teal-500/5 border border-teal-500/20 flex items-center justify-center text-teal-600 dark:text-teal-400 font-bold text-xs code-font">
                          {isProgressiveShapes ? "PS" : "TE"}
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-900 dark:text-white code-font group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                            {pkg.name}
                          </h3>
                          <span className="text-[9px] code-font text-slate-400 font-semibold tracking-wider uppercase">
                            {isPublished ? "npm package" : "in development"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isPublished ? (
                          <span className="px-2 py-0.5 rounded-full bg-green-500/10 dark:bg-green-500/5 text-green-600 dark:text-green-400 border border-green-500/20 text-[9px] font-bold uppercase code-font">
                            v1.0.4
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-amber-500/10 dark:bg-amber-500/5 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[9px] font-bold uppercase code-font">
                            In Dev
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                      {pkg.desc}
                    </p>
                  </div>

                  <PackageStats packageName={pkg.name} isPublished={isPublished} />

                  {/* Interactive live demo playground widget */}
                  <div className="w-full rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200/80 dark:border-slate-800 p-4 space-y-3 relative overflow-hidden">
                    <span className="absolute top-2 right-3 text-[8px] code-font text-slate-400 uppercase tracking-widest font-bold">Interactive Demo</span>
                    
                    {isProgressiveShapes ? (
                      /* Progressive Shapes demo */
                      <div className="flex flex-col md:flex-row items-center gap-4 py-2">
                        <div className="relative w-24 h-24 flex items-center justify-center">
                          {/* Animated SVG Ring representing a progressive shape */}
                          <svg className="w-full h-full transform -rotate-90">
                            <circle 
                              cx="48" 
                              cy="48" 
                              r="36" 
                              className="stroke-slate-200 dark:stroke-slate-900 fill-none" 
                              strokeWidth="5" 
                            />
                            <circle 
                              cx="48" 
                              cy="48" 
                              r="36" 
                              className="stroke-teal-500 dark:stroke-teal-400 fill-none transition-all duration-300" 
                              strokeWidth="5" 
                              strokeDasharray={2 * Math.PI * 36}
                              strokeDashoffset={2 * Math.PI * 36 * (1 - progressVal / 100)}
                              strokeLinecap="round" 
                            />
                          </svg>
                          <span className="absolute text-xs font-bold text-slate-800 dark:text-slate-200 code-font">{progressVal}%</span>
                        </div>
                        <div className="flex-1 w-full space-y-1.5 text-left">
                          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Adjust Progress Value</label>
                          <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={progressVal} 
                            onChange={(e) => setProgressVal(Number(e.target.value))}
                            className="w-full h-1 bg-slate-200 dark:bg-slate-900 rounded-lg appearance-none cursor-pointer accent-teal-500"
                          />
                          <p className="text-[10px] text-slate-500 leading-tight">
                            Zero-dependency SVG shape calculations rendering responsive React visual rings.
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Tiny Editor demo */
                      <div className="space-y-2 py-1 text-left">
                        {/* Editor Controls */}
                        <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-900 pb-1.5">
                          <button 
                            type="button"
                            onClick={() => setIsBold(!isBold)} 
                            className={`w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold border transition-colors ${isBold ? 'bg-teal-500 border-teal-500 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'}`}
                          >
                            B
                          </button>
                          <button 
                            type="button"
                            onClick={() => setIsItalic(!isItalic)} 
                            className={`w-5 h-5 flex items-center justify-center rounded text-[10px] italic font-bold border transition-colors ${isItalic ? 'bg-teal-500 border-teal-500 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'}`}
                          >
                            I
                          </button>
                          <button 
                            type="button"
                            onClick={() => setIsUnderline(!isUnderline)} 
                            className={`w-5 h-5 flex items-center justify-center rounded text-[10px] underline font-bold border transition-colors ${isUnderline ? 'bg-teal-500 border-teal-500 text-teal-600 dark:bg-teal-950/20 dark:text-teal-400' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'}`}
                          >
                            U
                          </button>
                          <div className="flex-1"></div>
                          <span className="text-[8px] code-font text-slate-400">{editorStats.words} words | {editorStats.chars} chars</span>
                        </div>
                        {/* Input Box */}
                        <textarea 
                          value={editorText}
                          onChange={(e) => setEditorText(e.target.value)}
                          className={`w-full p-2 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 outline-none text-[10px] min-h-[50px] leading-relaxed text-slate-700 dark:text-slate-300 resize-none ${isBold ? 'font-bold' : ''} ${isItalic ? 'italic' : ''} ${isUnderline ? 'underline' : ''}`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Copy command terminal-like section */}
                  <div className="space-y-2 text-left">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Installation</span>
                    <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 text-[10px] code-font text-teal-400 overflow-x-auto">
                      <div className="flex items-center gap-1.5 select-all">
                        <span className="text-slate-500">$</span>
                        <span>{pkg.installCmd}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyText(pkg.installCmd)}
                        className="p-1 rounded text-slate-400 hover:text-teal-400 hover:bg-slate-800 transition-all cursor-pointer"
                        title="Copy to clipboard"
                      >
                        {copiedText === pkg.installCmd ? <FaCheck size={10} className="text-teal-400" /> : <FaCopy size={10} />}
                      </button>
                    </div>
                  </div>

                  {/* Bottom Footer / Badges & Links */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-slate-100 dark:border-slate-900">
                    <div className="flex flex-wrap gap-1">
                      {pkg.tech.map((t, ti) => (
                        <span key={ti} className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-900 border border-slate-200/50 dark:border-slate-800/50 text-[9px] font-medium text-slate-500 dark:text-slate-400">
                          {t}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center gap-3.5 text-xs">
                      {pkg.npmLink ? (
                        <a 
                          href={pkg.npmLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-1 text-red-500 dark:text-red-400 hover:underline font-semibold"
                        >
                          <SiNpm size={14} /> npm
                        </a>
                      ) : null}
                      <a 
                        href={pkg.repo} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center gap-1 text-slate-700 dark:text-slate-300 hover:underline font-semibold"
                      >
                        <FaGithub size={13} /> GitHub
                      </a>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* TIKTOK / DEVELOPER CONTENT SECTION */}
        <section id="developer-content" className="space-y-8 text-start pt-12 border-t border-slate-200/50 dark:border-slate-800/50">
          <div className="space-y-2">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaTiktok className="text-teal-500" size={24} />
              Developer Content
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs md:text-sm max-w-xl">
              I share what I build and write, helping other developers level up.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-4 items-start">
            
            {/* Left Side: Creator Card Mockup */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5 w-full flex justify-center"
            >
              <div className="w-full max-w-[340px] rounded-3xl border border-slate-200/80 dark:border-slate-800/80 bg-white/40 dark:bg-slate-950/40 p-5 shadow-xl relative overflow-hidden group hover:border-teal-500/30 transition-all duration-300 glass">
                
                {/* Floating animated elements */}
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-teal-500/10 rounded-full blur-xl group-hover:bg-teal-500/20 transition-all duration-500"></div>
                <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-teal-500/5 rounded-full blur-xl"></div>
                
                <div className="flex flex-col items-center text-center space-y-4 relative z-10">
                  {/* Styled Avatar with pulsing indicator */}
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full p-1 bg-gradient-to-tr from-teal-500 to-indigo-500 animate-spin-slow">
                      <div className="w-full h-full rounded-full bg-white dark:bg-slate-950 flex items-center justify-center p-0.5">
                        <div className="w-full h-full rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-3xl">
                          <FaTiktok className="text-teal-400 animate-pulse" />
                        </div>
                      </div>
                    </div>
                    {/* Live Badge */}
                    <span className="absolute bottom-0 right-0 bg-red-500 text-white text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider shadow-md animate-bounce">
                      Active
                    </span>
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center justify-center gap-1.5">
                      @amidev2
                    </h3>
                    <span className="text-[10px] code-font text-teal-600 dark:text-teal-400 font-semibold uppercase tracking-wider">
                      Developer Content Creator
                    </span>
                  </div>

                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed px-2">
                    Continuously learning, building, and sharing concepts across frontend, Web3, and UI animation.
                  </p>

                  {/* Followers mockup stats */}
                  <div className="grid grid-cols-2 gap-4 w-full py-3 border-y border-slate-100 dark:border-slate-900/60 text-xs">
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Format</span>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Short Video</span>
                    </div>
                    <div>
                      <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block">Frequency</span>
                      <span className="font-bold text-slate-700 dark:text-slate-300">Weekly Tips</span>
                    </div>
                  </div>

                  {/* TikTok Follow Button */}
                  <motion.a 
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href="https://www.tiktok.com/@amidev2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 bg-slate-900 dark:bg-teal-600 hover:bg-slate-800 dark:hover:bg-teal-500 text-white dark:text-white rounded-full font-bold text-xs tracking-wider shadow-md flex items-center justify-center gap-2 transition-colors cursor-pointer"
                  >
                    <FaTiktok size={14} />
                    Follow me on TikTok →
                  </motion.a>
                </div>
              </div>
            </motion.div>

            {/* Right Side: Copy & Topic Badges & Video Cards */}
            <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                <div className="text-xs font-bold uppercase tracking-wider text-teal-600 dark:text-teal-400 code-font">
                  I BUILD. I LEARN. I SHARE.
                </div>
                
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Sharing the journey of building real products.
                </h3>
                
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  I share what I'm learning as a developer, break down technical concepts, and document my journey building real products.
                </p>

                {/* Topics list */}
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {["Web Development", "JavaScript", "React", "Next.js", "Web3", "Programming Concepts", "Developer Journey"].map((topic, ti) => (
                    <span 
                      key={ti} 
                      className="px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-900 border border-slate-200/50 dark:border-slate-800/50 text-[10px] font-medium text-slate-600 dark:text-slate-400"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Static Content Cards Container */}
              <div className="space-y-3.5 pt-4">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block code-font">🎥 Featured TikTok Concepts</span>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Card 1 */}
                  <motion.a 
                    whileHover={{ y: -4 }}
                    href="https://www.tiktok.com/@amidev2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-teal-500/20 transition-all flex flex-col justify-between space-y-4 group"
                  >
                    <div className="space-y-2">
                      <span className="text-[8px] font-bold text-teal-600 dark:text-teal-400 code-font uppercase">Development Patterns</span>
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                        Different Ways to Write Code
                      </h4>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        Comparing Imperative vs Declarative syntax, and Functional vs Object-Oriented paradigms.
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors flex items-center gap-1">
                      Watch on TikTok <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </motion.a>

                  {/* Card 2 */}
                  <motion.a 
                    whileHover={{ y: -4 }}
                    href="https://www.tiktok.com/@amidev2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 hover:border-teal-500/20 transition-all flex flex-col justify-between space-y-4 group"
                  >
                    <div className="space-y-2">
                      <span className="text-[8px] font-bold text-teal-600 dark:text-teal-400 code-font uppercase">Product Architecture</span>
                      <h4 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                        Inside a Flagship Audio Stack
                      </h4>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        Breaking down how we built Confess using Next.js, Node.js, and WebRTC for anonymous audio messaging.
                      </p>
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors flex items-center gap-1">
                      Watch on TikTok <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </motion.a>
                </div>

              </div>
            </div>

          </div>
        </section>
      </div>

      <WhatsAppLink />
    </div>
  );
}

export default Portfolio;
